/**
 * 通用 CORS 代理服務
 * 統一處理所有需要 CORS 代理的 API 請求
 *
 * 憑證類代理 URL 一律由環境變數提供(REACT_APP_*),
 * 絕不可將 email/apiKey/token 寫死於原始碼。
 */

import { logger } from '../utils/logger';

export interface CorsProxyConfig {
  name: string;
  url: string;
  type: "allorigins" | "x2u" | "codetabs" | "direct" | "custom";
  /** 自建代理的存取 token(僅 custom 類型使用,來自環境變數) */
  token?: string;
}

export interface CorsProxyOptions {
  timeout?: number; // 請求超時時間（毫秒）
  retryCount?: number; // 重試次數
  headers?: Record<string, string>; // 自定義請求頭
}

/**
 * 建立代理清單:私人代理(Workers/X2U)依環境變數決定是否啟用,
 * 未設定時自動跳過,僅保留公共代理。
 */
function buildProxyList(): CorsProxyConfig[] {
  const proxies: CorsProxyConfig[] = [];

  const workerUrl = process.env.REACT_APP_CORS_WORKER_URL;
  const workerToken = process.env.REACT_APP_CORS_WORKER_TOKEN;
  if (workerUrl && workerToken) {
    proxies.push({
      name: "Workers CORS",
      url: workerUrl,
      token: workerToken,
      type: "custom",
    });
  }

  const x2uUrl = process.env.REACT_APP_X2U_PROXY_URL;
  if (x2uUrl) {
    proxies.push({ name: "X2U CORS", url: x2uUrl, type: "x2u" });
  }

  proxies.push(
    { name: "AllOrigins", url: "https://api.allorigins.win/get?url=", type: "allorigins" },
    { name: "CodeTabs", url: "https://api.codetabs.com/v1/proxy?quest=", type: "codetabs" },
    { name: "CorsProxy.io", url: "https://corsproxy.io/?", type: "direct" },
  );

  return proxies;
}

export class CorsProxyService {
  private static instance: CorsProxyService;

  // CORS 代理服務列表 (按優先順序排列,私人代理需環境變數設定)
  private readonly CORS_PROXIES: CorsProxyConfig[] = buildProxyList();

  // 記錄哪個代理有效，避免重複嘗試
  private workingProxyIndex: number | null = null;
  private lastSuccessTime: number = 0;
  private readonly PROXY_CACHE_DURATION = 5 * 60 * 1000; // 5分鐘

  // 斷路器模式 - 避免瀑布式失敗
  private failureCount: number = 0;
  private lastFailureTime: number = 0;
  private readonly MAX_FAILURES = 5; // 最多失敗5次後開啟斷路器
  private readonly CIRCUIT_BREAKER_TIMEOUT = 60 * 1000; // 斷路器開啟1分鐘

  private constructor() {}

  /**
   * 獲取單例實例
   */
  static getInstance(): CorsProxyService {
    if (!CorsProxyService.instance) {
      CorsProxyService.instance = new CorsProxyService();
    }
    return CorsProxyService.instance;
  }

  /**
   * 使用 CORS 代理獲取資料，支援多個代理的 fallback
   */
  async fetch(
    targetUrl: string,
    options: CorsProxyOptions = {},
  ): Promise<Response> {
    // 檢查斷路器狀態
    const now = Date.now();
    if (this.failureCount >= this.MAX_FAILURES) {
      const timeSinceLastFailure = now - this.lastFailureTime;
      if (timeSinceLastFailure < this.CIRCUIT_BREAKER_TIMEOUT) {
        throw new Error(
          `CORS proxy circuit breaker is open. Too many failures (${this.failureCount}). Try again in ${Math.ceil((this.CIRCUIT_BREAKER_TIMEOUT - timeSinceLastFailure) / 1000)} seconds.`,
        );
      } else {
        // 重設斷路器
        this.failureCount = 0;
      }
    }

    const {
      timeout = 12000, // 縮短超時時間到12秒，提高回應速度
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      retryCount = 0,
      headers = {},
    } = options;

    let lastError: Error | null = null;

    // 如果有有效的代理，優先使用它
    if (
      this.workingProxyIndex !== null &&
      this.workingProxyIndex < this.CORS_PROXIES.length
    ) {
      // 如果上次成功時間距離現在超過緩存時間，重新嘗試所有代理
      if (Date.now() - this.lastSuccessTime > this.PROXY_CACHE_DURATION) {
        this.workingProxyIndex = null;
      } else {
        const cachedProxy = this.CORS_PROXIES[this.workingProxyIndex];
        // 注意:不得記錄完整 URL(含憑證),僅記錄代理名稱
        logger.api.debug(`使用快取代理: ${cachedProxy.name}`);
        try {
          const response = await this.fetchWithProxy(
            this.workingProxyIndex,
            targetUrl,
            { timeout, headers },
          );
          this.lastSuccessTime = Date.now();
          return response;
        } catch (error) {
          logger.api.warn(
            `快取代理 ${cachedProxy.name} 失敗,嘗試其他代理`,
            error instanceof Error ? error.message : error,
          );
          lastError = error instanceof Error ? error : new Error(String(error));

          // 上游 404/410 (資源不存在) 對所有代理結果都相同，直接以原錯誤終止
          // (保留 status，且不計入斷路器，避免影響其他請求)
          const terminalStatus = (error as any)?.status;
          if (terminalStatus === 404 || terminalStatus === 410) {
            throw error;
          }

          this.workingProxyIndex = null;
        }
      }
    }

    // 嘗試所有代理
    for (let i = 0; i < this.CORS_PROXIES.length; i++) {
      const proxy = this.CORS_PROXIES[i];
      logger.api.debug(`嘗試代理 ${i + 1}/${this.CORS_PROXIES.length}: ${proxy.name}`);

      try {
        const response = await this.fetchWithProxy(i, targetUrl, {
          timeout,
          headers,
        });

        // 記錄成功的代理
        logger.api.debug(`代理成功: ${proxy.name}`);
        this.workingProxyIndex = i;
        this.lastSuccessTime = Date.now();

        // 重設斷路器計數器
        if (this.failureCount > 0) {
          this.failureCount = 0;
        }

        return response;
      } catch (error) {
        logger.api.warn(
          `代理 ${proxy.name} 失敗:`,
          error instanceof Error ? error.message : error,
        );
        lastError = error instanceof Error ? error : new Error(String(error));

        // 上游 404/410 (資源不存在，如已下市代號) 對所有代理結果都相同，
        // 直接以原錯誤終止瀑布：保留 status 給上層判斷「不可重試」，
        // 且不計入斷路器失敗 (避免單一查無代號封鎖全部代理流量)
        const terminalStatus = (error as any)?.status;
        if (terminalStatus === 404 || terminalStatus === 410) {
          throw error;
        }

        // 如果不是最後一個代理，繼續嘗試下一個
        if (i < this.CORS_PROXIES.length - 1) {
          continue;
        }
      }
    }

    // 所有代理都失敗了 - 記錄斷路器失敗
    this.failureCount++;
    this.lastFailureTime = Date.now();

    // 簡化日誌信息，避免過度詳細的錯誤訊息
    const errorMessage =
      this.failureCount >= this.MAX_FAILURES
        ? `All CORS proxies failed (${this.failureCount} failures). Circuit breaker activated.`
        : `All CORS proxies failed (${this.failureCount}/${this.MAX_FAILURES}). Last error: ${lastError?.message || "Unknown"}`;

    // 保留最後一個錯誤的 status (如上游 5xx)，供上層 retry 條件判斷
    const finalError: any = new Error(errorMessage);
    if ((lastError as any)?.status) {
      finalError.status = (lastError as any).status;
    }
    throw finalError;
  }

  /**
   * 使用指定的代理呼叫 API
   */
  private async fetchWithProxy(
    proxyIndex: number,
    targetUrl: string,
    options: { timeout: number; headers: Record<string, string> },
  ): Promise<Response> {
    const proxy = this.CORS_PROXIES[proxyIndex];
    const { timeout, headers } = options;

    let url: string;
    let requestOptions: RequestInit = {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...headers,
      },
    };

    // 根據代理類型構建不同的 URL
    switch (proxy.type) {
      case "allorigins":
      case "x2u":
      case "codetabs":
        url = `${proxy.url}${encodeURIComponent(targetUrl)}`;
        break;
      case "direct":
        // direct 類型同樣需要編碼,否則目標 URL 的 query string
        // 會在第一個 & 處被代理截斷成自己的參數
        url = `${proxy.url}${encodeURIComponent(targetUrl)}`;
        break;
      case "custom": {
        // Workers CORS: 直接傳遞 URL + token(來自環境變數,不可記錄)
        if (!proxy.token) {
          throw new Error(`代理 ${proxy.name} 缺少 token 設定`);
        }
        url = `${proxy.url}${encodeURIComponent(targetUrl)}&token=${encodeURIComponent(proxy.token)}`;
        break;
      }
      default:
        throw new Error(`未知的代理類型: ${(proxy as any).type}`);
    }

    // 設定超時
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    requestOptions.signal = controller.signal;

    try {
      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        // 僅透明代理 (custom/direct) 的 HTTP 狀態碼代表上游 (可作為 404 終端判斷)；
        // x2u/codetabs 會包裝回應，其狀態碼為代理自身狀態，不附上以避免誤判
        const httpError = new Error(`HTTP ${response.status}: ${response.statusText}`) as any;
        if (proxy.type === "custom" || proxy.type === "direct") {
          httpError.status = response.status;
        }
        throw httpError;
      }

      // 根據代理類型解析回應
      return await this.parseProxyResponse(proxy, response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error(`${proxy.name} 請求超時 (${timeout}ms)`);
      }
      throw error;
    }
  }

  /**
   * 根據代理類型解析回應
   */
  private async parseProxyResponse(
    proxy: CorsProxyConfig,
    response: Response,
  ): Promise<Response> {
    switch (proxy.type) {
      case "allorigins": {
        const proxyResponse = await response.json();

        if (proxyResponse.status && proxyResponse.status.http_code === 200) {
          if (!proxyResponse.contents) {
            throw new Error(`${proxy.name} response contains empty contents`);
          }

          if (typeof proxyResponse.contents !== "string") {
            throw new Error(
              `Expected string contents, got ${typeof proxyResponse.contents}`,
            );
          }

          const originalData = JSON.parse(proxyResponse.contents);
          return new Response(JSON.stringify(originalData), {
            status: 200,
            statusText: "OK",
            headers: { "Content-Type": "application/json" },
          });
        } else {
          const proxyError: any = new Error(
            `${proxy.name} proxy error: ${proxyResponse.status?.error_message || "Unknown error"}`,
          );
          // 保留上游 HTTP 狀態碼 (如 Yahoo 404)，供上層判斷終端錯誤
          const httpCode = Number(proxyResponse.status?.http_code);
          if (Number.isFinite(httpCode) && httpCode > 0) {
            proxyError.status = httpCode;
          }
          throw proxyError;
        }
      }

      case "x2u":
      case "codetabs": {
        // 直接返回原始資料，不需要特殊解析
        const data = await response.json();
        return new Response(JSON.stringify(data), {
          status: 200,
          statusText: "OK",
          headers: { "Content-Type": "application/json" },
        });
      }

      case "direct":
      case "custom": {
        // 直接代理，返回原始回應
        return response;
      }

      default:
        throw new Error(`未知的代理類型: ${(proxy as any).type}`);
    }
  }

  /**
   * 重設連接方式偏好，強制重新嘗試所有代理
   */
  resetConnectionPreference(): void {
    this.workingProxyIndex = null;
    this.lastSuccessTime = 0;
  }

  /**
   * 取得目前使用的 CORS 代理
   */
  getCurrentConnectionMethod(): string | null {
    if (
      this.workingProxyIndex !== null &&
      this.workingProxyIndex < this.CORS_PROXIES.length
    ) {
      return this.CORS_PROXIES[this.workingProxyIndex].name;
    }
    return null;
  }

  /**
   * 手動設定首選的 CORS 代理(以代理名稱查找,避免索引錯位)
   */
  setPreferredConnectionMethod(
    method: "allorigins" | "x2u" | "codetabs" | "direct",
  ): void {
    const index = this.CORS_PROXIES.findIndex((proxy) => proxy.type === method);
    if (index === -1) {
      logger.api.warn(`找不到代理類型 ${method},維持現有連線偏好`);
      return;
    }
    this.workingProxyIndex = index;
    this.lastSuccessTime = Date.now();
  }

  /**
   * 取得服務狀態訊息
   */
  getServiceStatus() {
    const currentProxy =
      this.workingProxyIndex !== null
        ? this.CORS_PROXIES[this.workingProxyIndex]
        : null;

    return {
      currentProxy: currentProxy
        ? {
            index: this.workingProxyIndex,
            name: currentProxy.name,
            type: currentProxy.type,
          }
        : null,
      lastSuccessTime: this.lastSuccessTime,
      availableProxies: this.CORS_PROXIES.map((proxy, index) => ({
        index,
        name: proxy.name,
        type: proxy.type,
      })),
      proxyCacheDuration: this.PROXY_CACHE_DURATION,
    };
  }

  /**
   * 測試所有代理的可用性
   */
  async testAllProxies(testUrl: string = "https://httpbin.org/json"): Promise<
    Array<{
      proxy: CorsProxyConfig;
      success: boolean;
      responseTime: number;
      error?: string;
    }>
  > {
    const results = [];

    for (let i = 0; i < this.CORS_PROXIES.length; i++) {
      const proxy = this.CORS_PROXIES[i];
      const startTime = Date.now();

      try {
        await this.fetchWithProxy(i, testUrl, { timeout: 10000, headers: {} });
        const responseTime = Date.now() - startTime;
        results.push({
          proxy,
          success: true,
          responseTime,
        });
      } catch (error) {
        const responseTime = Date.now() - startTime;
        results.push({
          proxy,
          success: false,
          responseTime,
          error: error instanceof Error ? error.message : String(error),
        });
      }

      // 在測試之間添加短暫延遲
      if (i < this.CORS_PROXIES.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    return results;
  }
}

// 匯出單例實例
export const corsProxyService = CorsProxyService.getInstance();
export default corsProxyService;
